import React, { useEffect, useState, useCallback } from 'react';
import { Route, Plus, Save, Trash2, ArrowLeftRight } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const inp = 'w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[color:var(--brand-dark)] bg-white';

const PriceGrid = ({ vehicles, prices, onChange, prefix }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
    {vehicles.map((v) => (
      <div key={v.id}>
        <label className="text-[10px] text-gray-400 truncate block">{v.name}</label>
        <input type="number" step="1" min="0" placeholder="berechnet" value={prices[v.id] ?? ''} onChange={(e) => onChange({ ...prices, [v.id]: e.target.value })} data-testid={`${prefix}-price-${v.id}`} className={inp} />
      </div>
    ))}
  </div>
);

const RoutePricesTab = ({ vehicles, authHeaders }) => {
  const [routes, setRoutes] = useState([]);
  const [draft, setDraft] = useState({ from: 'Flughafen Zürich (ZRH)', to: '', both: true, prices: {} });
  const load = useCallback(() => fetch(`${BACKEND}/api/route-prices`).then((r) => r.json()).then(setRoutes).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.from.trim() || !draft.to.trim()) { toast({ title: 'Von und Nach erforderlich' }); return; }
    const res = await fetch(`${BACKEND}/api/admin/route-prices`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify(draft) });
    if (res.ok) { toast({ title: '✓ Festpreis-Route hinzugefügt' }); setDraft({ from: 'Flughafen Zürich (ZRH)', to: '', both: true, prices: {} }); load(); }
    else toast({ title: 'Speichern fehlgeschlagen' });
  };
  const save = async (r) => {
    const res = await fetch(`${BACKEND}/api/admin/route-prices/${r.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify({ from: r.from, to: r.to, both: r.both, prices: r.prices }) });
    toast({ title: res.ok ? '✓ Route gespeichert' : 'Speichern fehlgeschlagen' }); load();
  };
  const remove = async (r) => {
    if (!window.confirm(`Route ${r.from} → ${r.to} löschen?`)) return;
    await fetch(`${BACKEND}/api/admin/route-prices/${r.id}`, { method: 'DELETE', headers: authHeaders });
    toast({ title: '✓ Route gelöscht' }); load();
  };
  const upd = (id, patch) => setRoutes((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div className="space-y-6" data-testid="route-prices-tab">
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <h3 className="font-extrabold text-[color:var(--brand-dark)] mb-1 flex items-center gap-2"><Route className="w-5 h-5" style={{ color: 'var(--brand-accent)' }} /> Neue Festpreis-Route</h3>
        <p className="text-xs text-gray-500 mb-4">Ein Festpreis ersetzt die km-Berechnung für diese Strecke. Leere Felder = weiterhin nach km berechnet. Orte werden unscharf erkannt (z.B. «Zermatt» passt auf «Zermatt, Schweiz»; «ZRH» auf jeden Flughafen-Zürich-Text).</p>
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mb-3">
          <div><label className="text-[10px] text-gray-400">Von</label><input value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} data-testid="new-route-from" className={inp} /></div>
          <div><label className="text-[10px] text-gray-400">Nach</label><input placeholder="z.B. Zermatt" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} data-testid="new-route-to" className={inp} /></div>
          <label className="flex items-end gap-2 text-xs font-semibold text-[color:var(--brand-dark)] pb-2 cursor-pointer"><input type="checkbox" checked={draft.both} onChange={(e) => setDraft({ ...draft, both: e.target.checked })} data-testid="new-route-both" className="w-4 h-4 accent-[color:var(--brand-dark)]" /><ArrowLeftRight className="w-3.5 h-3.5" /> beide Richtungen</label>
        </div>
        <PriceGrid vehicles={vehicles} prices={draft.prices} onChange={(p) => setDraft({ ...draft, prices: p })} prefix="new-route" />
        <button onClick={add} data-testid="add-route-btn" className="accent-btn rounded-xl px-6 py-2.5 font-bold mt-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Hinzufügen</button>
      </div>

      <div className="space-y-3">
        {routes.length === 0 && <div className="bg-white rounded-2xl p-8 text-center text-gray-400" data-testid="routes-empty">Noch keine Festpreis-Routen.</div>}
        {routes.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl p-4 shadow-sm" data-testid={`route-row-${r.id}`}>
            <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mb-3">
              <input value={r.from} onChange={(e) => upd(r.id, { from: e.target.value })} className={`${inp} font-bold`} />
              <input value={r.to} onChange={(e) => upd(r.id, { to: e.target.value })} className={`${inp} font-bold`} />
              <label className="flex items-center gap-2 text-xs font-semibold text-[color:var(--brand-dark)] cursor-pointer"><input type="checkbox" checked={r.both !== false} onChange={(e) => upd(r.id, { both: e.target.checked })} className="w-4 h-4 accent-[color:var(--brand-dark)]" /><ArrowLeftRight className="w-3.5 h-3.5" /> beide Richtungen</label>
            </div>
            <PriceGrid vehicles={vehicles} prices={r.prices || {}} onChange={(p) => upd(r.id, { prices: p })} prefix={`route-${r.id}`} />
            <div className="flex items-center gap-2 mt-3">
              <button onClick={() => save(r)} data-testid={`save-route-${r.id}`} className="accent-btn rounded-lg px-4 py-1.5 text-xs font-bold flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Speichern</button>
              <button onClick={() => remove(r)} data-testid={`delete-route-${r.id}`} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoutePricesTab;
